import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { ChevronLeft, ChevronRight, Loader2, ArrowLeft, Mail, Phone, Building2, BriefcaseBusiness, CalendarClock, Cake } from "lucide-react";
import { toast } from "sonner";

function monthLabel(year, month0) {
  return new Date(year, month0, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

function ymStr(year, month0) {
  return `${year}-${String(month0 + 1).padStart(2, "0")}`;
}

function daysInMonth(year, month0) {
  return new Date(year, month0 + 1, 0).getDate();
}

const STATUS_COLOR = {
  present: "hsl(var(--status-present))",
  halfday: "hsl(var(--status-halfday))",
  absent:  "hsl(var(--status-absent))",
  leave:   "hsl(var(--status-leave))",
};

export default function EmployeeDetailPage() {
  const { id } = useParams();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month0, setMonth0] = useState(today.getMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const month = ymStr(year, month0);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/attendance/employee/${id}?month=${month}`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, month]);

  const changeMonth = (delta) => {
    let m = month0 + delta, y = year;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    setMonth0(m); setYear(y);
  };

  const emp = data?.employee;
  const summary = data?.summary || { present: 0, absent: 0, leave: 0, halfday: 0 };
  const days = data?.days || {};

  const initials = (emp?.name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();
  const totalDays = daysInMonth(year, month0);
  const firstWeekday = new Date(year, month0, 1).getDay(); // 0=Sun

  // build grid cells
  const cells = useMemo(() => {
    const arr = [];
    for (let i = 0; i < firstWeekday; i++) arr.push(null);
    for (let d = 1; d <= totalDays; d++) {
      const iso = `${year}-${String(month0 + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      arr.push({ day: d, iso, status: days[iso] });
    }
    return arr;
  }, [firstWeekday, totalDays, year, month0, days]);

  const attendanceRate = useMemo(() => {
    const marked = summary.present + summary.absent + summary.leave + summary.halfday;
    if (!marked) return 0;
    // half day counts as 0.5 present
    return Math.round(((summary.present + summary.halfday * 0.5) / marked) * 100);
  }, [summary]);

  return (
    <div className="p-8 lg:p-10" data-testid="employee-detail-page">
      <Link
        to="/presently/employees"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        data-testid="back-to-employees"
      >
        <ArrowLeft className="h-4 w-4" /> Back to employees
      </Link>

      {loading && !data ? (
        <div className="mt-10 text-muted-foreground text-sm flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading employee…
        </div>
      ) : !emp ? (
        <div className="mt-10 text-sm text-muted-foreground">Employee not found.</div>
      ) : (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile card */}
          <Card className="lg:col-span-1 p-6 rounded-2xl border-border shadow-soft">
            <div className="flex items-center gap-4">
              <Avatar className="h-14 w-14">
                <AvatarFallback className="text-base">{initials}</AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <div className="font-display text-2xl font-bold leading-tight truncate">{emp.name}</div>
                <div className="text-xs text-muted-foreground truncate">{emp.designation}</div>
              </div>
            </div>

            <div className="mt-6 space-y-3 text-sm">
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-muted-foreground" /> <span className="truncate">{emp.email}</span>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-muted-foreground" /> <span>{emp.phone}</span>
              </div>
              <div className="flex items-center gap-3">
                <Building2 className="h-4 w-4 text-muted-foreground" /> <span>{emp.department}</span>
              </div>
              <div className="flex items-center gap-3">
                <BriefcaseBusiness className="h-4 w-4 text-muted-foreground" /> <span>{emp.designation}</span>
              </div>
              <div className="flex items-center gap-3">
                <CalendarClock className="h-4 w-4 text-muted-foreground" /> <span className="font-mono-mini tabular-nums">{emp.joining_date}</span>
              </div>
              {emp.dob && (
                <div className="flex items-center gap-3">
                  <Cake className="h-4 w-4 text-muted-foreground" /> <span className="font-mono-mini tabular-nums">{emp.dob}</span>
                </div>
              )}
            </div>

            <div className="mt-6 pt-6 border-t border-border">
              <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">This month</div>
              <div className="grid grid-cols-4 gap-3 mt-3">
                {[
                  { k: "present", label: "Present" },
                  { k: "halfday", label: "Half"    },
                  { k: "absent",  label: "Absent"  },
                  { k: "leave",   label: "Leave"   },
                ].map(({ k, label }) => (
                  <div key={k} data-testid={`summary-${k}`}>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
                    <div
                      className="font-display text-2xl font-bold tabular-nums mt-1"
                      style={{ color: STATUS_COLOR[k] }}
                    >{summary[k]}</div>
                  </div>
                ))}
              </div>
              <div className="mt-5">
                <div className="flex justify-between text-[11px] text-muted-foreground uppercase tracking-wider">
                  <span>Attendance rate</span>
                  <span className="tabular-nums">{attendanceRate}%</span>
                </div>
                <div className="h-1.5 bg-secondary rounded-full mt-1.5 overflow-hidden">
                  <div
                    className="h-full transition-all"
                    style={{ width: `${attendanceRate}%`, background: STATUS_COLOR.present }}
                  />
                </div>
              </div>
            </div>
          </Card>

          {/* Calendar */}
          <Card className="lg:col-span-2 p-6 rounded-2xl border-border shadow-soft">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-mono-mini text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Calendar</div>
                <div className="font-display text-2xl font-bold mt-1" data-testid="month-label">
                  {monthLabel(year, month0)}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={() => changeMonth(-1)} data-testid="prev-month-btn" className="h-9 w-9">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={() => changeMonth(1)} data-testid="next-month-btn" className="h-9 w-9">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-7 gap-1.5 text-center text-[11px] uppercase tracking-wider text-muted-foreground">
              {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
                <div key={d} className="pb-2">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1.5">
              {cells.map((c, i) => c === null ? (
                <div key={`e-${i}`} />
              ) : (
                <div
                  key={c.iso}
                  data-testid={`day-cell-${c.iso}`}
                  className="aspect-square rounded-md border border-border flex flex-col items-center justify-between p-1.5 text-[11px]"
                  style={c.status ? {
                    background: `${STATUS_COLOR[c.status]}20`,
                    borderColor: `${STATUS_COLOR[c.status]}55`,
                  } : {}}
                >
                  <div className="w-full flex items-center justify-between">
                    <span className="tabular-nums font-mono-mini text-[11px] text-muted-foreground">{c.day}</span>
                    {c.status && (
                      <span
                        className="h-1.5 w-1.5 rounded-full"
                        style={{ background: STATUS_COLOR[c.status] }}
                      />
                    )}
                  </div>
                  <div
                    className="text-[10px] font-medium capitalize"
                    style={c.status ? { color: STATUS_COLOR[c.status] } : { color: "transparent" }}
                  >
                    {c.status || "·"}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-4 text-[11px] uppercase tracking-wider text-muted-foreground">
              <span className="inline-flex items-center gap-2">
                <span className="h-2 w-2 rounded-full" style={{ background: STATUS_COLOR.present }} /> Present
              </span>
              <span className="inline-flex items-center gap-2">
                <span className="h-2 w-2 rounded-full" style={{ background: STATUS_COLOR.halfday }} /> Half day
              </span>
              <span className="inline-flex items-center gap-2">
                <span className="h-2 w-2 rounded-full" style={{ background: STATUS_COLOR.absent }} /> Absent
              </span>
              <span className="inline-flex items-center gap-2">
                <span className="h-2 w-2 rounded-full" style={{ background: STATUS_COLOR.leave }} /> Leave
              </span>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
